/**
 * ApproveOrderDialog - Dialog for approving an order and assigning to project
 */
import React, { useState, useEffect } from 'react';
import { useOrderStore } from '../../stores/orderStore';
import { useProjectStore } from '../../stores';
import type { Order } from '../../types';
import '../Dialogs/Dialog.css';

interface ApproveOrderDialogProps {
  isOpen: boolean;
  order: Order | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const ApproveOrderDialog: React.FC<ApproveOrderDialogProps> = ({ 
  isOpen, 
  order, 
  onClose,
  onSuccess 
}) => {
  const { approveOrder } = useOrderStore();
  const { projects, fetchProjects } = useProjectStore();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [internalNotes, setInternalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && projects.length === 0) {
      fetchProjects();
    }
  }, [isOpen, projects.length, fetchProjects]);

  if (!isOpen || !order) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProjectId) {
      setError('Musíte vybrať projekt (obec/sieť)');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await approveOrder(order.id, selectedProjectId, internalNotes || undefined);
      handleClose();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Chyba pri schvaľovaní objednávky');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedProjectId(null);
    setInternalNotes('');
    setError(null);
    onClose();
  };

  const customerName = order.customer_type === 'individual' 
    ? `${order.first_name} ${order.last_name}`
    : order.company_name;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="dialog-header">
          <h2>✅ Schváliť objednávku</h2>
          <button className="dialog-close" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="dialog-content">
          {error && (
            <div className="error-message" style={{ 
              marginBottom: '1rem', 
              padding: '0.75rem', 
              backgroundColor: '#fee', 
              border: '1px solid #fcc', 
              borderRadius: '4px', 
              color: '#c00' 
            }}>
              {error}
            </div>
          )}

          {/* Order Info */}
          <div style={{ 
            padding: '1rem', 
            backgroundColor: '#f5f5f5', 
            borderRadius: '8px', 
            marginBottom: '1.5rem' 
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
              {customerName}
            </h3>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              <div><strong>Číslo objednávky:</strong> {order.order_number}</div>
              <div><strong>Adresa:</strong> {order.address}, {order.city}</div>
              <div><strong>Email:</strong> {order.email}</div>
              <div><strong>Telefón:</strong> {order.phone}</div>
              <div><strong>Služba:</strong> {order.service_type} {order.requested_speed && `(${order.requested_speed})`}</div>
              {order.customer_notes && (
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>Poznámky zákazníka:</strong>
                  <div style={{ 
                    marginTop: '0.25rem', 
                    padding: '0.5rem', 
                    backgroundColor: '#fff', 
                    borderRadius: '4px',
                    fontStyle: 'italic'
                  }}>
                    {order.customer_notes}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Project Selection */}
          <div className="form-group">
            <label style={{ fontWeight: 'bold', fontSize: '1rem' }}>
              Vybrať projekt (obec/sieť) *
            </label>
            <select
              value={selectedProjectId || ''}
              onChange={(e) => setSelectedProjectId(e.target.value ? parseInt(e.target.value) : null)}
              required
              style={{ 
                width: '100%', 
                padding: '0.75rem', 
                fontSize: '1rem',
                border: '2px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="">-- Vyberte projekt --</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name} {project.description && `(${project.description})`}
                </option>
              ))}
            </select>
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
              Vyberte projekt (obec/sieť), do ktorej bude zákazník pripojený
            </div>
          </div>

          {/* Internal Notes */}
          <div className="form-group">
            <label>Interné poznámky</label>
            <textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={3}
              placeholder="Voliteľné poznámky pre interné použitie..."
              style={{ 
                width: '100%', 
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '0.95rem'
              }}
            />
          </div>

          {/* Schválenie odteraz rovno vytvorí aj zákazníka — nech je to vidieť
              dopredu, aby nikto nehľadal ďalší krok, ktorý už neexistuje. */}
          <div style={{
            padding: '0.75rem 1rem',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            color: '#1e40af',
            lineHeight: 1.5,
          }}>
            Schválením sa z objednávky rovno stane <b>zákazník čakajúci na inštaláciu</b>{' '}
            vo zvolenom projekte. Montážnik si ho potom v kazete vyberie zo zoznamu.
            Konvertovať už netreba.
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={handleClose} disabled={isSubmitting}>
              Zrušiť
            </button>
            <button 
              type="submit" 
              className="btn-primary" 
              disabled={isSubmitting || !selectedProjectId}
              style={{ backgroundColor: '#28a745' }}
            >
              {isSubmitting ? 'Schvaľujem…' : '✅ Schváliť a vytvoriť zákazníka'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

